import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import AppBreadcrumbs from 'components/AppBreadcrumbs';
import PageLayout from 'layouts/PageLayout';
import NotFound from 'pages/NotFound';
import VerticalTabs from 'components/VerticalTabs';
import CompanySyncPanel from 'components/CompanySyncPanel';
import { useAsyncFn } from 'react-use';
import { makeStyles } from '@material-ui/core/styles';
import {
  getProvisionedTrainingModule,
  updateProvisionedTrainingModule,
} from 'http/hyperAdminProvisioned';
import { useRouteMatch, useHistory, useLocation } from 'react-router-dom';
import { routesAliases } from 'router/routesAliases';
import { useTranslation } from 'react-i18next';
import TrainingModuleForm from 'pages/TrainingModules/TrainingModuleForm';
import TrainingPartsTable from 'pages/TrainingModules/Edit/TrainingPartsTable';
import TrainingModuleFilesTable from 'pages/TrainingModules/Edit/TrainingModuleFilesTable';

const useStyles = makeStyles(theme => ({
  pageContent: {
    padding: theme.spacing(5, 5),
  },
}));

const persistReturnTab = returnTab => {
  if (returnTab != null) {
    localStorage.setItem('companyEditActiveTab', String(returnTab));
  }
};

const EditModule = ({ routeProps }) => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const routerMatch = useRouteMatch();
  const { t } = useTranslation('common');
  const { companyId, moduleId } = routerMatch.params;
  const [notFoundError, setNotFoundError] = useState(false);
  const [moduleData, setModuleData] = useState(null);
  const [activeTab, setActiveTab] = useState(location.state?.activeTab ?? 0);
  const backUrl = `${routesAliases.editCompany}/${companyId}`;
  const returnTab = location.state?.returnTab;

  const breadCrumbsItems = [
    {
      title: t(routeProps.parentTitle),
      link: routeProps.parentPath,
    },
    {
      title: t('routes.editCompany'),
      link: backUrl,
    },
    {
      title: moduleData?.name || t(routeProps.title),
      isActive: true,
    },
  ];

  const [fetchState, fetchModule] = useAsyncFn(async id => {
    const result = await getProvisionedTrainingModule(Number(companyId), id);
    if (!result.error) {
      setModuleData(result);
      setNotFoundError(false);
    } else if (result.statusCode === 404) {
      setNotFoundError(true);
    }
    return result;
  }, [companyId]);

  const [submittingState, saveModule] = useAsyncFn(async payload => {
    const result = await updateProvisionedTrainingModule(Number(companyId), moduleId, payload);
    if (!result.error && !result.statusCode) {
      persistReturnTab(returnTab);
      history.push(backUrl);
    }
    return result;
  }, [backUrl, companyId, history, moduleId, returnTab]);

  useEffect(() => {
    setNotFoundError(false);
    setModuleData(null);
    fetchModule(moduleId);
  }, [moduleId]);

  useEffect(() => {
    if (location.state?.activeTab !== undefined) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state?.activeTab]);

  const handleTabChange = (event, { tabIndex }) => {
    setActiveTab(tabIndex);
  };

  const tabsData = useMemo(
    () => [
      {
        label: t('trainingModules.tabs.general'),
        alias: 'general',
        Component: (
          <TrainingModuleForm
            mode="edit"
            layout="page"
            initialValues={moduleData}
            backUrl={backUrl}
            submitting={submittingState.loading}
            onSubmitForm={saveModule}
            onCancel={() => {
              persistReturnTab(returnTab);
              history.push(backUrl);
            }}
            readOnly={false}
          />
        ),
      },
      {
        label: t('trainingModules.tabs.theory'),
        alias: 'theory',
        Component: (
          <TrainingPartsTable
            trainingModuleId={moduleId}
            trainingModuleCode={moduleData?.code}
            trainingMethod="theory"
            readOnly={false}
            provisionedCompanyId={Number(companyId)}
          />
        ),
      },
      {
        label: t('trainingModules.tabs.practical'),
        alias: 'practical',
        Component: (
          <TrainingPartsTable
            trainingModuleId={moduleId}
            trainingModuleCode={moduleData?.code}
            trainingMethod="practical"
            readOnly={false}
            provisionedCompanyId={Number(companyId)}
          />
        ),
      },
      {
        label: t('trainingModules.tabs.ojt'),
        alias: 'ojt',
        Component: (
          <TrainingPartsTable
            trainingModuleId={moduleId}
            trainingModuleCode={moduleData?.code}
            trainingMethod="ojt"
            readOnly={false}
            provisionedCompanyId={Number(companyId)}
          />
        ),
      },
      {
        label: t('trainingModules.tabs.moduleFiles'),
        alias: 'moduleFiles',
        Component: (
          <TrainingModuleFilesTable
            trainingModuleId={moduleId}
            canAddFiles={!moduleData?.isFromSystem}
            canDeleteFiles={!moduleData?.isFromSystem}
            isSystemModule={Boolean(moduleData?.isFromSystem)}
          />
        ),
      },
    ],
    [backUrl, companyId, history, moduleData, moduleId, returnTab, saveModule, submittingState.loading, t],
  );

  if (notFoundError) {
    return <NotFound />;
  }

  return (
    <PageLayout
      pageTitle={<AppBreadcrumbs items={breadCrumbsItems} />}
      classes={{ content: classes.pageContent }}
      showLoader={fetchState.loading || !moduleData}
    >
      {moduleData && (
        <>
          {moduleData.isFromSystem ? (
            <CompanySyncPanel entityType="trainingModule" entityId={moduleId} onSynced={() => fetchModule(moduleId)} />
          ) : null}
          <VerticalTabs tabsData={tabsData} activeTab={activeTab} onTabChange={handleTabChange} />
        </>
      )}
    </PageLayout>
  );
};

EditModule.propTypes = {
  routeProps: PropTypes.shape({
    parentPath: PropTypes.string,
    parentTitle: PropTypes.string,
    title: PropTypes.string,
  }).isRequired,
};

export default EditModule;
